import { useEffect, useState } from 'react';
import { Dialog } from '../components/Dialog';
import { t } from '../i18n';
import { isBilltoro } from '../produkt';
import { ChipRadios } from './ChipRadios';
import type { Overview } from './types';
import './gdpdu.css';

// Export für die Betriebsprüfung (GDPdU, „wie bei Lexware“): ein Jahr wählen und das ZIP fürs Finanzamt herunterladen.
// Der Server setzt das ZIP beim Herunterladen zusammen; der Link lädt es direkt in eine Datei (auch große Firmen).

const BILLTORO = isBilltoro();

// Jahre mit Rechnungen, Billtoro auch mit Ausgaben oder Kassenbuch, und das laufende Jahr, neueste zuerst (auch für
// „Vorher sichern“ in billtoro/seiten/FirmaLoeschen.tsx)
export function jahreVon(data: Pick<Overview, 'items' | 'today' | 'ausgabenJahre'>): number[] {
  const jetzt = Number(data.today.slice(0, 4));
  const set = new Set([jetzt, ...data.items.map((i) => Number(i.invoiceDate.slice(0, 4))), ...(data.ausgabenJahre ?? [])]);
  return [...set].filter((j) => j >= 2000 && j <= jetzt).sort((a, b) => b - a);
}

// Geprüft wird meist ein abgeschlossenes Jahr: das Vorjahr, wenn es dort Rechnungen (Billtoro: oder Ausgaben) gibt,
// sonst das laufende
const vorschlag = (data: Pick<Overview, 'items' | 'today' | 'ausgabenJahre'>) => {
  const jetzt = Number(data.today.slice(0, 4));
  const vorjahr = data.items.some((i) => i.invoiceDate.startsWith(`${jetzt - 1}-`)) || !!data.ausgabenJahre?.includes(jetzt - 1);
  return vorjahr ? jetzt - 1 : jetzt;
};

export function GdpduDialog({ data, open, onClose }: { data: Overview; open: boolean; onClose(): void }) {
  const jahre = jahreVon(data);
  const [jahr, setJahr] = useState(() => vorschlag(data));
  const [geladen, setGeladen] = useState(false);

  useEffect(() => {
    if (open) setGeladen(false);
  }, [open]);

  const anzahl = data.items.filter((i) => i.invoiceDate.startsWith(`${jahr}-`)).length;
  const url = `/api/rechnungstool/${data.firm.id}/gdpdu?jahr=${jahr}`;

  return (
    <Dialog open={open} onClose={onClose} title={t('Für die Betriebsprüfung')} className="rt-exp-dlg gd-dlg">
      <p className="muted gd-lead">
        {BILLTORO
          ? 'Alle Rechnungen, Zahlungen, Mahnungen und Kunden eines Jahres, dazu die Ausgaben mit ihren Belegen und das Kassenbuch, als ZIP-Datei im Format fürs Finanzamt. Geben Sie die Datei so, wie sie ist, an die Prüferin oder den Prüfer oder an Ihr Steuerbüro.'
          : t(
              'Alle Rechnungen, Zahlungen, Mahnungen und Kunden eines Jahres als ZIP-Datei im Format fürs Finanzamt. Geben Sie die Datei so, wie sie ist, an die Prüferin oder den Prüfer oder an Ihr Steuerbüro.',
            )}
      </p>
      <div className="field">
        <span>{t('Jahr')}</span>
        <ChipRadios
          label={t('Jahr')}
          className="gd-jahre"
          value={String(jahr)}
          options={jahre.map((j) => [String(j), String(j)] as const)}
          onChange={(v) => {
            setJahr(Number(v));
            setGeladen(false);
          }}
        />
      </div>
      <p className="gd-count">
        {anzahl === 0
          ? t('Im Jahr {jahr} gibt es keine Rechnungen.', { jahr })
          : anzahl === 1
            ? t('1 Rechnung im Jahr {jahr}', { jahr })
            : t('{n} Rechnungen im Jahr {jahr}', { n: anzahl, jahr })}
      </p>
      {geladen && <p className="gd-done">{t('Die Datei wird erstellt und heruntergeladen. Bei vielen Rechnungen dauert das etwas.')}</p>}
      <div className="dlg-actions">
        <button type="button" className="btn" onClick={onClose}>
          {t('Schließen')}
        </button>
        <a className="btn primary" href={url} download onClick={() => setGeladen(true)}>
          {t('Herunterladen')}
        </a>
      </div>
    </Dialog>
  );
}
